import Tabs from '@allesauto/core/tabs';
import { PaymentType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import React from 'react';
import { useIntl } from 'react-intl';

import Cash from './Cash/Cash';
import FinancingForm, {
  FinancingFormValues,
} from '../forms/FinancingForm/FinancingForm';
import LeasingForm, {
  LeasingFormValues,
} from '../forms/LeasingForm/LeasingForm';

export interface PaymentOptionsProps {
  vehicleId: string;
  vehiclePrice: number;
  financingDetails: SchemaTypes.FinancingDetail[];
  selectedPaymentType: PaymentType;
  dealerId?: string;
  carConfiguratorId?: string;
  cashOptions?: SchemaTypes.CashOption[];
  financeCalculatorOptions?: SchemaTypes.FinanceCalculatorOptions | null;
  onCashSubmit?: (price: number) => void;
  onLeasingSubmit?: (values: LeasingFormValues) => void;
  onFinancingSubmit?: (values: FinancingFormValues) => void;
  setCalculateLoanSummary?:
    | React.Dispatch<React.SetStateAction<string>>
    | undefined;
}

export default function PaymentOptions({
  vehicleId,
  vehiclePrice,
  financingDetails: vehicleFinancingDetails = [],
  selectedPaymentType,
  cashOptions,
  financeCalculatorOptions,
  onCashSubmit,
  onLeasingSubmit,
  onFinancingSubmit,
  dealerId,
  carConfiguratorId,
  setCalculateLoanSummary,
}: PaymentOptionsProps) {
  const intl = useIntl();

  const className = {
    tabContentWrapper: 'mt-20px md:mt-30px pb-[140px]', // 140px bottom padding is to add space for the footer
  };

  const tabs = [];
  const tabsContent = [];

  // cash method is always presented
  tabs.push(translate(intl, 'ui.COMMON.PAYMENT_OPTION.CASH'));
  tabsContent.push(
    <Cash
      key={PaymentType.CashRange}
      price={vehiclePrice}
      options={cashOptions}
      onSubmit={onCashSubmit}
    />
  );

  const leasingDetails = vehicleFinancingDetails.find(
    (fd) => fd.type === PaymentType.LeasingRateRange
  );

  if (
    leasingDetails &&
    financeCalculatorOptions &&
    financeCalculatorOptions.leasing
  ) {
    tabs.push(translate(intl, 'ui.COMMON.PAYMENT_OPTION.LEASING'));
    tabsContent.push(
      <LeasingForm
        key={PaymentType.LeasingRateRange}
        options={financeCalculatorOptions.leasing as SchemaTypes.LeasingOptions}
        monthlyInstallment={leasingDetails.monthlyRate}
        summary={leasingDetails.summary}
        vehicleId={vehicleId}
        dealerId={dealerId}
        carConfiguratorId={carConfiguratorId}
        onSubmit={onLeasingSubmit}
      />
    );
  }

  const financingDetails = vehicleFinancingDetails.find(
    (fd) => fd.type === PaymentType.LoanRateRange
  );

  if (
    financingDetails &&
    financeCalculatorOptions &&
    financeCalculatorOptions.loan
  ) {
    tabs.push(translate(intl, 'ui.COMMON.PAYMENT_OPTION.FINANCING'));
    tabsContent.push(
      <FinancingForm
        key={PaymentType.LoanRateRange}
        options={financeCalculatorOptions.loan as SchemaTypes.LoanOptions}
        monthlyInstallment={financingDetails.monthlyRate}
        summary={financingDetails.summary}
        finalInstallment={financingDetails.finalPayment}
        vehicleId={vehicleId}
        dealerId={dealerId}
        carConfiguratorId={carConfiguratorId}
        onSubmit={onFinancingSubmit}
        setCalculateLoanSummary={setCalculateLoanSummary}
      />
    );
  }

  // in case of just one payment method we don't show tabs
  if (tabsContent.length === 1) {
    return tabsContent[0];
  }

  let defaultSelectedIndex = 0;

  if (selectedPaymentType === PaymentType.LeasingRateRange) {
    defaultSelectedIndex = 1;
  } else if (selectedPaymentType === PaymentType.LoanRateRange) {
    defaultSelectedIndex = tabsContent.length - 1;
  }

  return (
    <Tabs
      tabs={tabs}
      defaultSelectedIndex={defaultSelectedIndex}
      fullWidth
      contentWrapperClassName={className.tabContentWrapper}
    >
      {tabsContent}
    </Tabs>
  );
}
